// /// <reference types="@types/google.maps" />
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnDestroy,
    Output,
    ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FleetTrackerUtilityService } from '../fleet-tracker-utility.service';
import { FleetTrackerConfig } from '../fleet-tracker.config';
import { FleetTrackerService } from '../fleet-tracker.service';
import { MapScriptLoaderService } from '../map-script-loader.service';
import { monochromeMapOptions } from '../map/MapStyles';
import {
    FenceLayerModel,
    LayerIndex,
    MapOptionsModel,
    MarkerLayerModel,
    PointModel,
    ZoomIndex
} from '../model/models';
import { Activation } from '../services/device-geofence.service';

@Component({
    selector: 'google-maps-wrapper',
    templateUrl: './google-maps-wrapper.component.html',
    styleUrls: ['./google-maps-wrapper.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [FleetTrackerService]
})
export class GoogleMapsWrapperComponent implements AfterViewInit, OnDestroy { // T is effectively "any" on a component

    private markerLayer: LayerIndex;

    @Input() set geofenceActivations(layer: MarkerLayerModel<Activation>) {
        this.addMarkerLayer(LayerIndex.Marker, layer);
    }

    @Input() set geofence(layer: FenceLayerModel<any>) {
        this.addGeofenceLayer(LayerIndex.Fence, layer);
    }

    @Input() set radius(radius: number) {
        this.setRadius(radius);
    }

    @Input() set center(point: PointModel) {
        this.setCenter(point);
    }

    @Input() set bounds(bounds: PointModel[]) {
        this.setBounds(bounds);
    }

    @Input() set options(options: MapOptionsModel) {
        this.setOptions(options);
    }

    @Input()
    styles = {}

    // To toggle between showing all History Trackpoints and showing when traces are hovered
    @Input() showHistoryTrackpoints: boolean = false;

    @Output() onMarkerClick = new EventEmitter<google.maps.Marker>();
    @Output() onHistoryMarkerClick = new EventEmitter<google.maps.Marker>();
    @Output() onBorderClick = new EventEmitter<google.maps.Polyline | google.maps.Marker>();
    // @Output() onEnter = new EventEmitter<google.maps.Marker>();
    // @Output() onLeave = new EventEmitter<google.maps.Marker>();
    @Output() onMapClick = new EventEmitter<google.maps.LatLng>();
    @Output() onMapReady = new EventEmitter<google.maps.Map>();
    @Output() onMapZoom = new EventEmitter<number>();

    @ViewChild('googleMapContainer') googleMapContainer: any;

    private _onMarkerClickSubscription: Subscription;
    private _onHistoryMarkerClickSubscription: Subscription;
    private _onBorderClickSubscription: Subscription;
    // private _onEnterSubscription: Subscription;
    // private _onLeaveSubscription: Subscription;
    private _onMapClickSubscription: Subscription;
    private _onMapZoomSubscription: Subscription;
    private _onLoadSubscription: Subscription;

    private _options: MapOptionsModel = {};
    private _center: PointModel;
    private _radius: number;
    private _bounds: PointModel[];
    private _padding: google.maps.Padding = { top: 0, bottom: 0, left: 0, right: 0 };
    map: google.maps.Map;

    constructor(private _config: FleetTrackerConfig,
        private _fleetTrackerService: FleetTrackerService,
        private _utility: FleetTrackerUtilityService,
        private _loader: MapScriptLoaderService,
        private _zone: NgZone) {
    }

    // if no map defer action
    private _deferredLayers = [];
    private _deferredCenter: PointModel;
    private _deferredBounds: PointModel[];
    private _deferredRadius: number;
    private _deferredPadding: google.maps.Padding;
    private _deferredOptions: MapOptionsModel;

    private focusTrails: boolean;
    // UI determined values
    private OPACITY_LEVEL = 0.35;

    ngAfterViewInit() { // <-- ViewChild queries guaranteed to be resolved here
        if (this._config.apiKeyLoader) { // optional run-time support for key retrieval
            this._config.apiKeyLoader().then(key => {
                this._config.apiKey = key;
                this.loadGoogleMapScript();
            });
        } else {
            this.loadGoogleMapScript();
        }
    }

    private loadGoogleMapScript() {
        this._zone.runOutsideAngular(() => {
            this._onLoadSubscription = this._loader.load().subscribe(() => {
                this.initialize();
                this._fleetTrackerService.googleMap = new google.maps.Map(this.googleMapContainer.nativeElement, this.getMapOptions());
                this.processDeferredActions(); // process map dependent inputs
                this.onMapReady.emit(this._fleetTrackerService.googleMap);
                this._loader.mapReady.emit(this._fleetTrackerService.googleMap);
                this._loader.googleMap = this._fleetTrackerService.googleMap;
                this.map = this._fleetTrackerService.googleMap;
            }, (error) => {
                this.onMapReady.error(error);
            });
        });
    }

    private initialize(): void {
        this._onMarkerClickSubscription = this._fleetTrackerService.onMarkerClick.subscribe(marker => this.onMarkerClickHandler(marker), this.onSubscriptionError);
        // this._onHistoryMarkerClickSubscription = this._fleetTrackerService.onHistoryMarkerClick.subscribe(marker => this.onHistoryMarkerClickHandler(marker), this.onSubscriptionError);
        this._onBorderClickSubscription = this._fleetTrackerService.onBorderClick.subscribe(border => this.onBorderClickHandler(border), this.onSubscriptionError);
        // this._onEnterSubscription = this._fleetTrackerService.onEnter.subscribe(marker => this.onEnter.emit(marker), this.onSubscriptionError);
        // this._onLeaveSubscription = this._fleetTrackerService.onLeave.subscribe(marker => this.onLeave.emit(marker), this.onSubscriptionError);
        this._onMapClickSubscription = this._fleetTrackerService.onMapClick.subscribe(coords => this.onMapClickHandler(coords), this.onSubscriptionError);
        this._onMapZoomSubscription = this._fleetTrackerService.onMapZoom.subscribe(zoom => this.onMapZoom.emit(zoom), this.onSubscriptionError);
    }

    private getMapOptions(): google.maps.MapOptions {
        return monochromeMapOptions;
    }

    private onMarkerClickHandler(marker): void {
        this.onMarkerClick.emit(marker);
    }

    private onBorderClickHandler(border): void {
        this.onBorderClick.emit(border);
    }

    private onMapClickHandler(coords): void {
        this.onMapClick.emit(coords);
        // this._fleetTrackerService.removeMarkerLayer(this.markerLayer);
    }

    private onSubscriptionError(error: any) {
        console.log(error);
    }

    // The map cannot be created until we have a child DOM element to bind it with.
    // The map has a getDiv() but there's no setDiv(). We defer action when any map
    // dependent input is set when there is no available map.
    //
    private processDeferredActions() {
        if (this._deferredCenter) {
            this.setCenter(this._deferredCenter);
            this._deferredCenter = undefined;
        }

        if (this._deferredRadius) {
            // this._fleetTrackerService.setRadius(this._deferredRadius, this._padding);
            this._deferredRadius = undefined;
        }

        if (this._deferredBounds) {
            this._fleetTrackerService.setBounds(this._deferredBounds, this._padding);
            this._deferredRadius = undefined;
        }

        if (this._deferredPadding) {
            if (this._radius) {
                // this._fleetTrackerService.setRadius(this._radius, this._deferredPadding);
            } else if (this._bounds) {
                this._fleetTrackerService.setBounds(this._bounds, this._deferredPadding);
            }
            this._deferredPadding = undefined;
        }

        if (this._deferredLayers.length > 0) {
            this._deferredLayers.forEach(action => action());
            this._deferredLayers = [];
        }

        if (this._deferredOptions) {
            this._fleetTrackerService.googleMap.setOptions(this.getMapOptions());
            this._deferredOptions = undefined;
        }
    }

    ngOnDestroy() {
        if (this._onMarkerClickSubscription) {
            this._onMarkerClickSubscription.unsubscribe();
            // this._onEnterSubscription.unsubscribe();
            // this._onLeaveSubscription.unsubscribe();
            this._onMapClickSubscription.unsubscribe();
            this._onMapZoomSubscription.unsubscribe();
        }

        if (this._onHistoryMarkerClickSubscription) {
            this._onHistoryMarkerClickSubscription.unsubscribe();
        }

        if (this._onBorderClickSubscription) {
            this._onBorderClickSubscription.unsubscribe();
        }

        if (this._onLoadSubscription) {
            this._onLoadSubscription.unsubscribe();
        }
    }

    private setCenter(point: PointModel) {
        if (point) {
            this._center = point;
            this._bounds = undefined;
            if (this._fleetTrackerService.googleMap) {
                this._fleetTrackerService.googleMap.setCenter({ lat: point.lat, lng: point.lng });
            } else {
                this._deferredCenter = point;
            }
        }
    }

    private setRadius(radius: number) {
        if (radius) {
            this._radius = radius;
            this._bounds = undefined;
            if (this._fleetTrackerService.googleMap) {
                // this._fleetTrackerService.setRadius(radius, this._padding);
            } else {
                this._deferredRadius = radius;
            }
        }
    }

    private setBounds(points: PointModel[]) {
        if (points) {
            this._bounds = points;
            this._radius = undefined;
            if (this._fleetTrackerService.googleMap) {
                if (points.length) {
                    this._fleetTrackerService.setBounds(points, this._padding);
                }
            } else {
                this._deferredBounds = points;
            }
        }
    }

    private setPadding(padding: google.maps.Padding) {
        this._padding = padding;
        if (padding) {
            if (this._fleetTrackerService.googleMap) {
                if (this._radius && this._center) {
                    this._fleetTrackerService.googleMap.setCenter(this._center);
                    // this._fleetTrackerService.setRadius(this._radius, padding);
                } else if (this._bounds && this._bounds.length) {
                    this._fleetTrackerService.setBounds(this._bounds, padding);
                }
            } else {
                this._deferredPadding = padding;
            }
            this._fleetTrackerService.setPadding(padding);
        }
    }

    private setOptions(options: MapOptionsModel): void {
        this._options = options;
        if (this._fleetTrackerService.googleMap) {
            const options = this.getMapOptions();
            options.zoom = 7;
            // delete options.zoom;
            delete options.mapTypeId;
            this._fleetTrackerService.googleMap.setOptions(options);
        } else {
            this._deferredOptions = options;
        }
    }

    public setPosition(bounds: PointModel[]);
    public setPosition(center: PointModel, radius: number);

    // We expose this for the special case for user panning and zooming the map outside of our control
    public setPosition(centerOrBounds: PointModel | PointModel[], radius?: number) {
        if (centerOrBounds instanceof Array) {
            this.setBounds(centerOrBounds);
        } else {
            this.setCenter(centerOrBounds);
            this.setRadius(radius);
        }
    }

    private addMarkerLayer(index: LayerIndex, layer: MarkerLayerModel<any>) {
        if (layer) {
            if (this._fleetTrackerService.googleMap) {
                this.markerLayer = index;
                this._fleetTrackerService.removeMarkerLayer(index);
                this._fleetTrackerService.addMarkerLayer(layer, index);
                this._fleetTrackerService.showMarkerLayer(index, true)
            } else {
                this._deferredLayers.push(() => this.addMarkerLayer(index, layer));
            }
        }
    }

    private addGeofenceLayer(index: LayerIndex, layer: FenceLayerModel<any>) {
        if (this._fleetTrackerService.googleMap) {
            if (layer && !layer.showMarker) { //To add Geofence shape
                this._fleetTrackerService.removeFenceLayer(index);
                this._fleetTrackerService.addFenceLayer(layer);
                this._fleetTrackerService.showFenceLayer(index, true);
            } else { // To add Geofence marker
                this._fleetTrackerService.removeFenceLayer(index);
                if (layer) {
                    this._fleetTrackerService.addFenceMarker(layer);
                }
            }
        } else {
            this._deferredLayers.push(() => this.addGeofenceLayer(index, layer));
        }
    }

    panTo(lat: number, lng: number) {
        this._fleetTrackerService.googleMap.panTo({ lat: lat, lng: lng });
    }

    setZoom(index: ZoomIndex) {
        this._fleetTrackerService.googleMap.setZoom(index);
    }
}
